// Compact topbar for the Freehand workspace. Includes a workspace switcher
// so users can jump back to the geometric Studio and vice-versa.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Download, Sparkles, LogIn, LogOut, Lock, Boxes, PenLine, FolderOpen, Save, ShoppingBag, Shield } from 'lucide-react';
import { useFreehand } from '@/lib/freehandStore';
import { useAuth } from '@/lib/auth';
import AdminDrawer from '@/components/admin/AdminDrawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WorkspaceSwitcher({ current }) {
  return (
    <div className="segmented h-8 text-xs" data-testid="workspace-switcher">
      <Link
        to="/"
        aria-pressed={current === 'studio'}
        data-testid="workspace-switcher-studio"
        className={`px-3 flex items-center gap-1.5 ${current === 'studio' ? 'font-semibold' : ''}`}
      >
        <Boxes className="w-3.5 h-3.5" /> Studio
      </Link>
      <Link
        to="/draw"
        aria-pressed={current === 'draw'}
        data-testid="workspace-switcher-draw"
        className={`px-3 flex items-center gap-1.5 ${current === 'draw' ? 'font-semibold' : ''}`}
      >
        <PenLine className="w-3.5 h-3.5" /> Freehand
      </Link>
    </div>
  );
}

export default function FreehandTopbar({ onExport, onOpenUpgrade, onOpenGallery, captureFn }) {
  const panelCount = useFreehand((s) => s.panels.length);
  const panels = useFreehand((s) => s.panels);
  const getMesh = useFreehand((s) => s.getMesh);
  const getColors = useFreehand((s) => s.getColors);
  const referenceImage = useFreehand((s) => s.referenceImage);
  const { user, isFreehandPro, isAdmin, login, logout } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('My Freehand Ball');
  const [saving, setSaving] = useState(false);
  // If a name collision is detected we surface a small confirm before
  // actually calling the (idempotent) save endpoint. `null` = no prompt;
  // otherwise the object carries just enough context to run the real save
  // when the user confirms.
  const [overwriteConfirm, setOverwriteConfirm] = useState(null);

  const buildPayload = () => {
    const mesh = getMesh();
    // The reference image dataUrl can dominate the payload size. Mongo's
    // per-document BSON cap is 16 MB; if the base64 alone is > ~10 MB we
    // skip it (rather than fail the whole save) and warn the user so they
    // can re-upload a smaller photo. New uploads are auto-downscaled to
    // ~500 KB in the LeftRail, so this branch only fires for legacy state.
    let refPayload = null;
    let refSkipped = false;
    if (referenceImage) {
      const url = referenceImage.dataUrl || '';
      if (url.length > 10_000_000) {
        refSkipped = true;
      } else {
        refPayload = {
          dataUrl: url,
          rotationY: referenceImage.rotationY || 0,
          scaleX: referenceImage.scaleX ?? 1,
          scaleY: referenceImage.scaleY ?? 1,
        };
      }
    }
    return {
      payload: {
        name: saveName || 'Untitled',
        template: 'freehand',
        panel_count: panels.length,
        vertices: mesh.vertices,
        faces: mesh.faces,
        colors: getColors(),
        seam_width: 0.02,
        finish: 'matte',
        thumbnail: captureFn ? captureFn() : null,
        smooth_vertices: [],
        freehand_panels: panels.map((p) => ({
          points: p.points,
          color: p.color,
          smooth: p.smooth || [],
          group: p.group,
        })),
        reference_image: refPayload,
      },
      refSkipped,
    };
  };

  const persistSave = async ({ payload, refSkipped, overwriting }) => {
    setSaving(true);
    try {
      await axios.post(`${API}/designs`, payload);
      toast.success(
        overwriting
          ? refSkipped
            ? `Overwrote "${payload.name}" (reference photo too large — re-upload a smaller image to include it)`
            : `Overwrote "${payload.name}"`
          : refSkipped
            ? 'Saved (reference photo too large — re-upload a smaller image to include it)'
            : 'Saved to gallery',
      );
      setSaveOpen(false);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) toast.error('Sign in to save designs');
      else if (status === 413) toast.error('Design too large — try a smaller reference image');
      else toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doSave = async () => {
    const targetName = (saveName || 'Untitled').trim();
    // Ask the gallery whether a design already exists under this name so we
    // can prompt the user before the endpoint silently overwrites it.
    let collision = false;
    try {
      const { data } = await axios.get(`${API}/designs`);
      collision = Array.isArray(data) && data.some((d) => d.name === targetName);
    } catch {
      // Auth issues or network hiccup — let the real POST surface the error.
    }
    const built = buildPayload();
    if (collision) {
      setOverwriteConfirm({ ...built, name: targetName });
      return;
    }
    persistSave({ ...built, overwriting: false });
  };

  return (
    <>
    <header
      className="h-14 shrink-0 border-b border-slate-200 bg-white/80 backdrop-blur-xl flex items-center px-2 sm:px-4 gap-2 sm:gap-3"
      data-testid="freehand-topbar"
    >
      <div className="flex items-center gap-2 sm:pr-3 sm:border-r sm:border-slate-200 min-w-0">
        <div className="w-9 h-9 flex items-center justify-center overflow-hidden shrink-0">
          <img src="/logo.png" alt="Tudor Balls" className="w-9 h-9 object-contain" />
        </div>
        <div className="leading-tight hidden sm:block">
          <div className="text-sm font-semibold text-slate-900 tracking-tight">Tudor Balls</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Freehand Draw</div>
        </div>
      </div>

      <a
        href="https://shop.tudorballs.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="freehand-topbar-shop-link"
        className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-[#007AFF] px-2.5 py-1 rounded-full border border-slate-200 hover:border-[#007AFF]/40 transition-colors"
        title="Buy hand-made balls at shop.tudorballs.com"
      >
        <ShoppingBag className="w-3.5 h-3.5" />
        Shop hand-made balls
      </a>

      <WorkspaceSwitcher current="draw" />

      <div className="ml-auto flex items-center gap-1">
        <Button
          variant="ghost"
          className="rounded-full h-9 px-2 sm:px-3"
          onClick={onOpenGallery}
          data-testid="freehand-btn-gallery"
          title="Gallery"
        >
          <FolderOpen className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Gallery</span>
        </Button>
        <Button
          variant="ghost"
          className="rounded-full h-9 px-2 sm:px-3"
          onClick={() => { if (!user) { toast.info('Sign in to save designs'); login(); return; } setSaveOpen(true); }}
          disabled={panelCount === 0}
          data-testid="freehand-btn-save"
          title="Save design"
        >
          <Save className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Save</span>
        </Button>
        <Button
          className={`rounded-full ml-1 h-9 text-white px-2 sm:px-4 ${isFreehandPro ? 'bg-[#007AFF] hover:bg-[#0062CC]' : 'bg-slate-900 hover:bg-slate-800'}`}
          onClick={onExport}
          data-testid="freehand-btn-export"
        >
          {isFreehandPro ? <Download className="w-4 h-4 sm:mr-2" /> : <Lock className="w-4 h-4 sm:mr-2" />}
          <span className="hidden sm:inline">Export</span>
        </Button>
        {!isFreehandPro && (
          <Button
            className="rounded-full ml-1 h-9 bg-[#007AFF] hover:bg-[#0062CC] text-white px-2 sm:px-4"
            onClick={onOpenUpgrade}
            data-testid="freehand-btn-upgrade"
          >
            <Sparkles className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Upgrade · $29.99</span>
            <span className="sm:hidden text-xs font-semibold">$29.99</span>
          </Button>
        )}
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full ml-1 h-9 w-9 p-0 border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => setAdminOpen(true)}
            title="Admin"
            data-testid="freehand-btn-admin"
          >
            <Shield className="w-4 h-4" />
          </Button>
        )}
        {user ? (
          <button
            onClick={logout}
            className="ml-1 sm:ml-2 flex items-center gap-2 pl-1 pr-1 sm:pr-2 h-9 rounded-full border border-slate-200 hover:bg-slate-50"
            data-testid="freehand-btn-logout"
            title={user.email}
          >
            {user.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-slate-300" />
            )}
            <LogOut className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
          </button>
        ) : (
          <Button
            variant="ghost"
            className="rounded-full ml-1 h-9 px-2 sm:px-4"
            onClick={login}
            data-testid="freehand-btn-login"
          >
            <LogIn className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Sign in</span>
          </Button>
        )}
      </div>
    </header>
    {isAdmin && <AdminDrawer open={adminOpen} onOpenChange={setAdminOpen} />}
    <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="freehand-save-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight">Save to gallery</DialogTitle>
          <DialogDescription>Pick a name — you can reload this design any time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Design name"
            data-testid="freehand-save-name-input"
            className="rounded-xl"
          />
          <div className="text-[11px] text-slate-500">
            {panelCount} panel{panelCount === 1 ? '' : 's'} · saved with per-panel colours, corner smoothing and symmetry groups.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setSaveOpen(false)} data-testid="freehand-save-cancel">Cancel</Button>
          <Button onClick={doSave} disabled={saving || !saveName.trim()} data-testid="freehand-save-confirm" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* Overwrite confirmation. Fires only when the user tries to save with
        a name that already exists in their gallery. */}
    <Dialog open={!!overwriteConfirm} onOpenChange={(o) => !o && setOverwriteConfirm(null)}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="freehand-overwrite-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight">Overwrite existing design?</DialogTitle>
          <DialogDescription>
            A design named <span className="font-semibold text-slate-900">“{overwriteConfirm?.name}”</span> already
            exists in your gallery. Saving will replace it — this can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOverwriteConfirm(null)}
            disabled={saving}
            data-testid="freehand-overwrite-cancel"
          >
            Keep both — rename
          </Button>
          <Button
            onClick={async () => {
              const c = overwriteConfirm;
              if (!c) return;
              setOverwriteConfirm(null);
              await persistSave({ payload: c.payload, refSkipped: c.refSkipped, overwriting: true });
            }}
            disabled={saving}
            data-testid="freehand-overwrite-confirm"
            className="rounded-full bg-red-600 hover:bg-red-700 text-white"
          >
            {saving ? 'Overwriting…' : 'Overwrite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
